import { useId, useState } from 'react';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { FormStatusMessage } from '../components/FormStatusMessage';
import { Honeypot } from '../components/Honeypot';
import { Icon } from '../components/Icon';
import { Picture } from '../components/Picture';
import { ProjectCard } from '../components/portfolio/ProjectCard';
import { projectsByMonograph, type ProjectCategory } from '../data/projects';
import { useFormSubmit } from '../lib/forms';
import { Seo, breadcrumbLd } from '../lib/seo';
import { SITE, absoluteUrl } from '../lib/site';
import { container } from '../lib/ui';

type Filter = 'all' | ProjectCategory;

const FILTERS: Array<{ value: Filter; label: string }> = [
  { value: 'all', label: 'All Works' },
  { value: 'residential', label: 'Private Sanctuaries' },
  { value: 'office', label: 'Executive Offices' },
  { value: 'dining', label: 'Culinary & Dining' },
];

const matches = (filter: Filter, categories: ProjectCategory[]) => filter === 'all' || categories.includes(filter);
const pad = (n: number) => String(n).padStart(2, '0');

const MATERIAL_SPLIT = [
  { label: 'Stone', title: 'Sourced Natural Stones & Marble', value: 44, color: 'bg-primary' },
  { label: 'Timber', title: 'Certified Reclaimed Teak & Hardwoods', value: 32, color: 'bg-emerald-vivid' },
  { label: 'Metals', title: 'Architectural Bronzes & Metals', value: 14, color: 'bg-gold-dark' },
  { label: 'Textiles', title: 'Tactile Natural Textiles', value: 10, color: 'bg-secondary' },
];

const ENGAGEMENTS = [
  'Request Bound Archival Monograph (Print)',
  'Commission Private Estate Walkthrough',
  'New Turnkey Residential Architectural Brief',
  'Executive Corporate Headquarters Inquiry',
];

const TRAIL: Array<[string, string]> = [['Portfolio', '/portfolio']];

const inputClass =
  'w-full bg-surface-cream rounded px-space-sm py-space-xs font-body-sm text-body-sm text-on-surface placeholder:text-on-surface-variant/60 focus:outline-none focus:ring-2 focus:ring-gold-dark/50 focus:bg-surface transition-colors';
const labelClass = 'font-label-sm text-label-sm uppercase text-on-surface-variant';

function EnquiryForm() {
  const { status, error, onSubmit } = useFormSubmit('enquiry');
  const id = useId();
  return (
    <div className="w-full lg:w-[420px] shrink-0 bg-surface-ivory rounded-xl p-space-md sm:p-space-lg text-on-surface shadow-xl flex flex-col gap-space-md">
      <div className="flex flex-col gap-space-xs">
        <h3 className="font-headline-sm text-headline-sm text-primary">Private Enquiry</h3>
        <p className="font-body-sm text-body-sm text-on-surface-variant">Select your preferred avenue of engagement:</p>
      </div>
      <form className="relative flex flex-col gap-space-sm" onSubmit={onSubmit}>
        <Honeypot />
        <div className="flex flex-col gap-space-xs">
          <label htmlFor={`${id}-name`} className={labelClass}>
            Full Name &amp; Title
          </label>
          <input
            id={`${id}-name`}
            name="name"
            type="text"
            required
            autoComplete="name"
            placeholder="e.g., Dr. S. Ramanathan"
            className={inputClass}
          />
        </div>
        <div className="flex flex-col gap-space-xs">
          <label htmlFor={`${id}-contact`} className={labelClass}>
            Confidential Contact (Email or Mobile)
          </label>
          <input
            id={`${id}-contact`}
            name="contact"
            type="text"
            required
            placeholder="contact@domain.com or +91..."
            className={inputClass}
          />
        </div>
        <div className="flex flex-col gap-space-xs">
          <label htmlFor={`${id}-engagement`} className={labelClass}>
            Nature of Engagement
          </label>
          <select id={`${id}-engagement`} name="engagement" className={inputClass} defaultValue={ENGAGEMENTS[0]}>
            {ENGAGEMENTS.map((e) => (
              <option key={e}>{e}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-space-xs">
          <label htmlFor={`${id}-message`} className={labelClass}>
            Notes <span className="normal-case tracking-normal">(optional)</span>
          </label>
          <textarea
            id={`${id}-message`}
            name="message"
            rows={3}
            placeholder="Project location, typology or preferred dates…"
            className={`${inputClass} resize-y`}
          />
        </div>
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="mt-space-xs w-full bg-primary hover:bg-primary-container text-gold-light hover:text-on-primary font-label-lg text-label-lg uppercase tracking-wider py-space-sm rounded transition-all duration-300 flex items-center justify-center gap-space-xs shadow-md disabled:opacity-60 disabled:cursor-wait"
        >
          <span>{status === 'submitting' ? 'Transmitting…' : 'Transmit Studio Request'}</span>
          <Icon name="arrow_forward" size={18} />
        </button>
        <FormStatusMessage
          status={status}
          error={error}
          successText="Your request has been recorded. Our principal team will be in touch shortly."
        />
      </form>
      <div className="pt-space-xs flex flex-wrap items-center justify-between gap-x-space-sm text-on-surface-variant font-label-sm text-label-sm">
        <span>Direct Studio Helpline:</span>
        <a className="text-primary font-semibold hover:text-emerald-vivid transition-colors" href={`tel:${SITE.phoneE164}`}>
          {SITE.phoneDisplay}
        </a>
      </div>
    </div>
  );
}

export default function Portfolio() {
  const [filter, setFilter] = useState<Filter>('all');
  const visible = projectsByMonograph.filter((p) => matches(filter, p.categories));
  const listId = useId();

  return (
    <>
      <Seo
        title="Interior Design Portfolio"
        description="Explore Green Valley Interiors' portfolio of villas, penthouses, master suites, executive offices, kitchens and restaurants designed across South India."
        keywords={[
          'interior design portfolio in Nagercoil',
          'luxury villa interior projects Tamil Nadu',
          'residential interior design case studies',
          'office interior design projects Kanyakumari',
          'modular kitchen design projects Nagercoil',
          'restaurant interior design portfolio Kerala',
        ]}
        path="/portfolio"
        jsonLd={[
          breadcrumbLd(TRAIL),
          {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: 'Green Valley Interiors — Selected Works',
            numberOfItems: projectsByMonograph.length,
            itemListElement: projectsByMonograph.map((p, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              name: p.title,
              url: absoluteUrl(`/portfolio/${p.slug}`),
            })),
          },
        ]}
      />

      {/* CURATORIAL INTRO */}
      <section className="relative w-full bg-surface-ivory pt-space-lg md:pt-space-xl pb-space-lg">
        <div className={`${container} flex flex-col gap-space-lg`}>
          <Breadcrumbs trail={TRAIL} />
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-space-md">
            <div className="flex flex-col gap-space-xs max-w-3xl">
              <div className="flex items-center gap-space-xs">
                <span className="w-8 h-px bg-secondary" />
                <span className="font-label-sm text-label-sm uppercase tracking-widest text-secondary">
                  Architectural Archive · 2021—2025
                </span>
              </div>
              <h1 className="flex flex-col gap-space-xs animate-rise">
                <span className="font-label-md text-label-md uppercase tracking-[0.2em] text-emerald-vivid">
                  Interior Design Portfolio<span className="sr-only"> — </span>
                </span>
                <span className="font-headline-xl text-headline-xl-mobile md:text-headline-xl text-primary font-normal tracking-tight">
                  Selected Works &amp; Built Sanctuaries
                </span>
              </h1>
              <p className="font-body-lg text-body-lg text-on-surface-variant max-w-2xl pt-space-xs animate-rise [animation-delay:80ms]">
                Turnkey luxury residential transformations, refined executive suites, and bespoke architectural
                insertions across Southern India, balanced between organic quietude and uncompromising tectonic
                precision.
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-1 lg:grid-cols-2 gap-space-sm sm:gap-space-md shrink-0">
              <div className="bg-surface-cream rounded-lg p-space-md flex flex-col gap-space-xs shadow-sm">
                <div className="flex items-center gap-space-xs text-primary">
                  <Icon name="space_dashboard" size={20} className="text-emerald-vivid" />
                  <span className="font-headline-sm text-headline-sm font-semibold">185,000+</span>
                </div>
                <span className="font-label-sm text-label-sm uppercase text-on-surface-variant">Delivered Commissioned Sq Ft</span>
              </div>
              <div className="bg-surface-cream rounded-lg p-space-md flex flex-col gap-space-xs shadow-sm">
                <div className="flex items-center gap-space-xs text-primary">
                  <Icon name="verified" size={20} className="text-emerald-vivid" />
                  <span className="font-headline-sm text-headline-sm font-semibold">Zero-Punchlist</span>
                </div>
                <span className="font-label-sm text-label-sm uppercase text-on-surface-variant">Quality Guarantee Standard</span>
              </div>
            </div>
          </div>

          {/* FILTER RIBBON */}
          <div className="pt-space-md flex flex-col lg:flex-row lg:items-center justify-between gap-space-md">
            <div className="flex flex-wrap items-center gap-space-xs" role="group" aria-label="Filter projects by type">
              {FILTERS.map((f) => {
                const active = filter === f.value;
                const count = projectsByMonograph.filter((p) => matches(f.value, p.categories)).length;
                return (
                  <button
                    key={f.value}
                    type="button"
                    aria-pressed={active}
                    aria-controls={listId}
                    onClick={() => setFilter(f.value)}
                    className={`px-space-md py-space-xs rounded font-label-md text-label-md uppercase tracking-wider transition-all duration-300 ${
                      active ? 'bg-primary text-gold-light shadow-sm' : 'bg-surface-cream text-on-surface hover:bg-surface-container-high'
                    }`}
                  >
                    {f.label} <span aria-hidden="true">[{pad(count)}]</span>
                    <span className="sr-only">({count} projects)</span>
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-space-md text-on-surface-variant font-label-sm text-label-sm uppercase tracking-widest">
              <span className="flex items-center gap-space-xs">
                <Icon name="tune" size={16} className="text-secondary" />
                <span>Index Order: Architectural Chronology</span>
              </span>
              <span className="hidden sm:inline-block w-1.5 h-1.5 rounded-full bg-secondary" />
              <span className="hidden sm:inline-block" aria-live="polite">
                Displaying {visible.length} Case Stud{visible.length === 1 ? 'y' : 'ies'}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* MONOGRAPH GRID */}
      <section className="w-full bg-surface py-space-xl" aria-label="Project monographs">
        <div className={container}>
          <ul id={listId} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-gutter">
            {visible.map((p) => (
              <li key={p.slug} className="flex">
                <ProjectCard project={p} className="w-full" />
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* MATERIAL INTEGRITY */}
      <section className="w-full bg-surface-cream py-space-xl md:py-20">
        <div className={`${container} grid grid-cols-1 lg:grid-cols-12 gap-gutter items-center`}>
          <div className="reveal lg:col-span-5 flex flex-col gap-space-md">
            <span className="font-label-sm text-label-sm uppercase tracking-widest text-secondary">Material Integrity &amp; Sourcing</span>
            <h2 className="font-headline-lg text-headline-lg-mobile md:text-headline-lg text-primary">Form Grounded in Endemic Substance</h2>
            <p className="font-body-md text-body-md text-on-surface-variant">
              Every project in our register operates as an architectural dialogue between regional microclimate conditions
              and generational stone and wood craftsmanship. We specify natural limestones, reclaimed timbers, and
              non-volatile plant-based finishes that age with profound dignity.
            </p>
            <div className="bg-surface-ivory rounded-lg p-space-md shadow-sm flex flex-col gap-space-sm">
              <div className="flex items-center justify-between gap-space-sm font-label-sm text-label-sm uppercase">
                <span className="text-primary font-bold">Turnkey Material Distribution</span>
                <span className="text-emerald-vivid">100% Traceable</span>
              </div>
              <div
                className="w-full h-3 rounded-full bg-surface-container flex overflow-hidden"
                role="img"
                aria-label={MATERIAL_SPLIT.map((m) => `${m.value}% ${m.title}`).join(', ')}
              >
                {MATERIAL_SPLIT.map((m) => (
                  <div key={m.label} className={`${m.color} h-full`} style={{ width: `${m.value}%` }} title={`${m.title} (${m.value}%)`} />
                ))}
              </div>
              <ul className="grid grid-cols-2 sm:grid-cols-4 gap-space-xs font-label-sm text-label-sm pt-space-xs" aria-hidden="true">
                {MATERIAL_SPLIT.map((m) => (
                  <li key={m.label} className="flex items-center gap-space-xs">
                    <span className={`w-2.5 h-2.5 rounded-full ${m.color}`} />
                    <span className="text-on-surface-variant">
                      {m.value}% {m.label}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-space-md sm:pb-6">
            <figure className="reveal relative rounded-xl overflow-hidden shadow-md h-72">
              <Picture
                name="project-travertine-oak-study"
                alt="Honed travertine stone meeting dark smoked oak joinery with a hairline brass inlay"
                sizes="(min-width: 1024px) 28vw, (min-width: 640px) 50vw, 100vw"
                className="block w-full h-full"
                imgClassName="w-full h-full object-cover"
              />
              <figcaption className="absolute bottom-0 inset-x-0 p-space-sm bg-gradient-to-t from-botanical-dark/90 to-transparent font-label-sm text-label-sm uppercase tracking-wider text-gold-light">
                Detail · Joinery Precision
              </figcaption>
            </figure>
            <figure className="reveal relative rounded-xl overflow-hidden shadow-md h-72 sm:translate-y-6">
              <Picture
                name="project-sanctuary-basin"
                alt="Hand-carved stone washbasin with a dark bronze wall tap against textured lime plaster"
                sizes="(min-width: 1024px) 28vw, (min-width: 640px) 50vw, 100vw"
                className="block w-full h-full"
                imgClassName="w-full h-full object-cover"
              />
              <figcaption className="absolute bottom-0 inset-x-0 p-space-sm bg-gradient-to-t from-botanical-dark/90 to-transparent font-label-sm text-label-sm uppercase tracking-wider text-gold-light">
                Detail · Water Sculptures
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* PRIVATE ENQUIRY */}
      <section
        id="enquiry"
        className="w-full bg-botanical-dark text-on-primary py-space-xl md:py-20 relative overflow-hidden"
      >
        <div className="absolute -right-24 -bottom-24 w-96 h-96 rounded-full bg-tertiary-container/30 blur-3xl pointer-events-none" />
        <div className="absolute left-1/4 -top-24 w-80 h-80 rounded-full bg-emerald-vivid/10 blur-3xl pointer-events-none" />
        <div className={`${container} relative z-10`}>
          <div className="bg-surface-cream/5 backdrop-blur-md rounded-2xl p-space-md sm:p-space-lg md:p-space-xl shadow-2xl flex flex-col lg:flex-row items-start lg:items-center justify-between gap-space-xl">
            <div className="flex flex-col gap-space-sm max-w-2xl">
              <div className="flex items-center gap-space-xs">
                <span className="w-6 h-px bg-gold-light" />
                <span className="font-label-sm text-label-sm uppercase tracking-widest text-gold-light">Direct Studio Engagement</span>
              </div>
              <h2 className="font-headline-lg text-headline-lg-mobile md:text-headline-lg text-on-primary leading-tight">
                Commission a Private Walkthrough or Request the Complete Archival Folio
              </h2>
              <p className="font-body-lg text-body-lg text-gold-light/80">
                Green Valley conducts selective consultations for bespoke estates, penthouses, and executive commissions
                throughout Tamil Nadu, Karnataka, and Kerala. Receive our hardbound linen monograph or schedule an
                intimate architectural dialogue.
              </p>
              <div className="flex flex-wrap items-center gap-space-md pt-space-xs text-gold-light font-label-sm text-label-sm uppercase">
                <span className="flex items-center gap-space-xs">
                  <Icon name="shield" size={18} className="text-emerald-vivid" />
                  <span>Complete Client Confidentiality</span>
                </span>
                <span className="flex items-center gap-space-xs">
                  <Icon name="home_pin" size={18} className="text-emerald-vivid" />
                  <span>Nagercoil Atelier &amp; Site Labs</span>
                </span>
              </div>
            </div>
            <EnquiryForm />
          </div>
        </div>
      </section>
    </>
  );
}
